import React, { useMemo, useCallback, useEffect, FC, useRef } from "react"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import type { SchemaFormRefProps } from "resha"
import { Loader, ButtonGroup } from "dash-styled"

import { MainThunkDispatch } from "../../../../../../../../../Reducers"
import { AppDetailBase } from "../../../../AppDetailBase/AppDetailBase"
import { useAppInstance } from "../../../../../../../../../Hooks/useAppInstance"
import { useAppTemplate } from "../../../../../../../../../Hooks/useAppTemplate"
import {
    getValue,
    hasResult,
    hasValue
} from "../../../../../../../../../Models/Client/FetchState"
import { SimpleInfoBox } from "../../../../../../../../Common/SimpleInfoBox/SimpleInfoBox"
import {
    findFirstError,
    Result
} from "../../../../../../../../../Models/Utility/Result"
import { useBoolean } from "../../../../../../../../../Hooks/useBoolean"
import { updateInstance } from "../../../../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { routePaths } from "../../../../../../../../../Routes"
import { useGroundTruthTotalCount } from "../../../../../../../../../Hooks/useGroundTruthTotalCount"
import {
    dirtinessChanged,
    clearInputsForId
} from "../../../../../../../../../Actions/Client/InputRender/InputRenderActions"

import { useJobCount } from "../../../../../../../../../Hooks/useJobCount"
import { APIJobsPerStatusModel } from "../../../../../../../../../Models/API/APIJobsPerStatusModel"
import { hasAppPermission } from "../../../../../../../../../Models/Client/ProjectRole"
import { useAssumedRoles } from "../../../../../../../../../Hooks/useAssumedRoles"
import { useAppState } from "../../../../../../../../../Hooks/useReduxState"
import { useAppStatus } from "../../../../../../../../../Hooks/useAppStatus"
import { useAppSuperTasks } from "../../../../../../../../../Hooks/useAppSuperTasks"
import { selectHasFreemiumExpired } from "../../../../../../../../../Helpers/Functions/Selectors/auth"
import { useSchemaFormOutput } from "../../../../../../../../../Hooks/useSchemaFormOutput"
import { getParameterSchema } from "../../../../../../../../../Models/API/AppTemplateModel"
import { TabButton } from "../../../../../../../../Common/Button/TabButton"
import { ModalView } from "../ModalView"
import { JobSettings } from "./JobSettings"

interface AppCustomizeJobParametersProps {
    orgUsername: string
    appID: string
}

export const AppCustomizeJobParameters: FC<AppCustomizeJobParametersProps> = ({
    orgUsername,
    appID
}) => {
    const formRef = useRef<SchemaFormRefProps>(null)
    const [isRequestingChange, startRequestingChange, endRequestingChange] =
        useBoolean(false)
    const [liveValidateForm, startLiveValidatingForm, stopLiveValidatingForm] =
        useBoolean(false)
    const [isSubmitting, startSubmitting, endSubmitting] = useBoolean(false)

    const [saving, startSaving, endSaving] = useBoolean(false)

    const instanceFetch = useAppInstance(appID)

    const templateFetch = useAppTemplate(appID)

    const { value: superTasks } = useAppSuperTasks(appID)

    const appStatusFetch = useAppStatus(appID)

    const [jobFormData, onJobFormDataChange] = useSchemaFormOutput(
        getValue(instanceFetch)?.appParams?.params
    )

    const dispatch = useDispatch<MainThunkDispatch>()

    const updateProject = useCallback(
        (
            appID: string,
            params: any,
            successMessage: string,
            errorMessage: string
        ) =>
            dispatch(
                updateInstance(appID, params, successMessage, errorMessage)
            ).catch(() => {}),
        [dispatch]
    )

    const rolesFetch = useAssumedRoles(appID)
    const roles = useMemo(() => getValue(rolesFetch) ?? [], [rolesFetch])
    const [userExpiryContact, showUserExpiryContact, hideUserExpiryContact] =
        useBoolean(false)
    const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)

    const changeParams = useMemo(
        () =>
            hasAppPermission(roles, "task-design:update") &&
            hasValue(instanceFetch) &&
            JSON.stringify(jobFormData) !==
                JSON.stringify(instanceFetch.value.appParams?.params),
        [roles, instanceFetch, jobFormData]
    )

    const totalCountFetch = useGroundTruthTotalCount(appID)

    const [
        updateDataPointModalEnabled,
        updateDataPointModalEnabledTrue,
        updateDataPointModalEnabledFalse
    ] = useBoolean(false)
    const [
        showUpdateDataPointModal,
        showUpdateDataPointModalTrue,
        showUpdateDataPointModalFalse
    ] = useBoolean(false)

    const jobCount = useJobCount(appID)
    const totalJobCount = useMemo(
        () => (jobCount ? APIJobsPerStatusModel.total(jobCount) : 0),
        [jobCount]
    )
    const paramsSchemaFetch = useMemo(
        () =>
            hasValue(templateFetch)
                ? Result.ok(getParameterSchema(templateFetch.value))
                : undefined,
        [templateFetch]
    )

    useEffect(() => {
        if (totalJobCount !== undefined && totalJobCount > 0) {
            updateDataPointModalEnabledTrue()
        } else {
            updateDataPointModalEnabledFalse()
        }
    }, [
        totalJobCount,
        updateDataPointModalEnabledFalse,
        updateDataPointModalEnabledTrue
    ])

    /**
     * Final update project callback.
     * @param extraParams used in modal submit callback, e.g. cancelAndRelabelJobsInState param can be passed
     * @param callback used as onfulfilled callback, e.g. hides update data point modal
     */
    const handleSubmitJobSettings = useCallback(
        (extraParams = {}, callback = () => {}) => {
            if (!hasValue(instanceFetch)) {
                return
            }
            const { uuid, appParams } = instanceFetch.value
            startSaving()
            updateProject(
                uuid,
                {
                    appParams: {
                        params: {
                            ...appParams?.params,
                            ...jobFormData
                        }
                    },
                    superTasks,
                    ...extraParams
                },
                "Job design changes saved",
                "Job design change failed"
            )
                .then(callback)
                .finally(() => {
                    endSaving()
                    stopLiveValidatingForm()
                })
        },
        [
            startSaving,
            endSaving,
            stopLiveValidatingForm,
            updateProject,
            superTasks,
            instanceFetch,
            jobFormData
        ]
    )

    const handleTriggerSubmit = useCallback(() => {
        if (hasFreemiumPlanExpired) {
            return showUserExpiryContact()
        }

        if (changeParams) {
            if (getValue(totalCountFetch) === 0) {
                if (updateDataPointModalEnabled) {
                    showUpdateDataPointModalTrue()
                    startSubmitting()
                } else {
                    handleSubmitJobSettings()
                }
            } else {
                startRequestingChange()
                startSubmitting()
            }
        }
    }, [
        hasFreemiumPlanExpired,
        changeParams,
        showUserExpiryContact,
        totalCountFetch,
        updateDataPointModalEnabled,
        showUpdateDataPointModalTrue,
        startSubmitting,
        startRequestingChange,
        handleSubmitJobSettings
    ])

    const switchToTaskParameters = () => {
        dispatch(
            push(routePaths.appCustomizeTaskParameters(orgUsername, appID))
        )
    }

    const noSave = useMemo(
        () => !changeParams && !isRequestingChange,
        [isRequestingChange, changeParams]
    )

    const isDocumentExtraction =
        hasValue(paramsSchemaFetch) &&
        paramsSchemaFetch.value?.uiSchema["ui:field"] ===
            "https://widgets.super.ai/document-extraction-parameter@1"

    const render = () => {
        if (
            !hasResult(instanceFetch) ||
            !hasResult(templateFetch) ||
            !hasValue(paramsSchemaFetch)
        ) {
            return <Loader />
        }

        if (instanceFetch.isError || templateFetch.isError) {
            return (
                <SimpleInfoBox variant="alert">
                    Error fetching data.
                    {findFirstError(instanceFetch, templateFetch)}
                </SimpleInfoBox>
            )
        }

        const instance = instanceFetch.value
        const template = templateFetch.value

        if (paramsSchemaFetch.value === undefined) return <></>

        return (
            <div
                className="w-full flex flex-col items-center relative"
                style={{
                    height: `calc(100% - ${
                        hasValue(appStatusFetch) && appStatusFetch.value
                            ? "204px"
                            : "104px"
                    })`
                }}
            >
                <ButtonGroup
                    equalWidth={false}
                    className="w-full h-11 min-h-11 mb-6"
                >
                    <TabButton active className="py-2">
                        Job Settings
                    </TabButton>
                    <TabButton
                        active={false}
                        onClick={switchToTaskParameters}
                        className="py-2"
                    >
                        Task Settings
                    </TabButton>
                </ButtonGroup>
                <JobSettings
                    appID={appID}
                    disabled={!hasAppPermission(roles, "task-design:update")}
                    noSave={noSave}
                    saving={saving}
                    formRef={formRef}
                    formData={jobFormData}
                    schemaForm={paramsSchemaFetch.value}
                    instance={instance}
                    template={template}
                    onChange={onJobFormDataChange}
                    onSubmit={handleTriggerSubmit}
                    liveValidationState={[
                        liveValidateForm,
                        startLiveValidatingForm
                    ]}
                />
            </div>
        )
    }

    useEffect(() => {
        if (changeParams !== undefined) dispatch(dirtinessChanged(changeParams))
    }, [dispatch, changeParams])

    useEffect(() => {
        return () => {
            dispatch(clearInputsForId(appID))
        }
    }, [dispatch, appID])

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title="Project Settings"
            breadcrumbs={[]}
            fixedContentWidth={
                hasValue(paramsSchemaFetch) ? !isDocumentExtraction : true
            }
            render={render}
            renderModalView={() => (
                <ModalView
                    appID={appID}
                    orgUsername={orgUsername}
                    jobFormData={jobFormData}
                    isSubmitting={isSubmitting}
                    isRequestingChange={isRequestingChange}
                    saving={saving}
                    showUpdateDataPoint={showUpdateDataPointModal}
                    showUpdateDataPointModal={showUpdateDataPointModalTrue}
                    endRequestingChange={endRequestingChange}
                    startSaving={startSaving}
                    endSaving={endSaving}
                    stopLiveValidatingForm={stopLiveValidatingForm}
                    endSubmitting={endSubmitting}
                    hideUpdateDataPointModal={showUpdateDataPointModalFalse}
                    onSubmit={handleSubmitJobSettings}
                />
            )}
            forceUserExpiryContact={userExpiryContact}
            resetUserExpiryContact={hideUserExpiryContact}
        />
    )
}
